
import { DEFAULT_LANGUAGE } from '@/constants'
import { dateHelper } from '@/helpers/dateHelper'
import {
  addDays,
  addMonths,
  compareAsc,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  getDate,
  getDay,
  getDaysInMonth,
  getMonth,
  getYear,
  startOfMonth,
  sub,
  subDays,
  subMonths,
} from 'date-fns'
import Vue from 'vue'

interface Slot {
  id: string
  start: string
  end: string
  isInitialSlot: boolean
}

// @ts-nocheck
export default Vue.extend({
  name: 'appointment-card',
  props: {
    selectedSlot: {
      type: Object as () => Slot,
      required: false,
      default: null as Slot | null,
    },
    initialSelectedSlot: {
      type: Object as () => Slot,
      required: false,
      default: null as Slot | null,
    },
    availableSlots: {
      type: Array as () => Slot[],
      required: true,
      default: () => [] as Slot[],
    },
    isLoadingSlots: {
      type: Boolean,
      default: false,
    },
    deliveryDate: {
      type: String,
      required: false,
      default: null,
    },
    pickingDates: {
      type: Object as () => { start: string; end: string } | null,
      required: false,
      default: null,
    },
    language: {
      type: String,
      required: false,
      default: DEFAULT_LANGUAGE,
    },
    minDeliveryPromiseDate: {
      type: String,
      required: false,
      default: null as string | null,
    },
    unavailableDates: {
      type: Array as () => Array<{ date: string; reasons: string[] }>,
      required: false,
      default: () => [] as Array<{ date: string; reasons: string[] }>,
    },
  },
  data: () => ({
    month: null as number | null,
    year: null as number | null,
    currentDate: dateHelper.getDate(),
    localSelectedDay: null as null | string,
    selectedSlotId: null as string | null,
  }),
  computed: {
    daysTrads(): { [key: string]: string } {
      return {
        monday: this.$t('common.week.monday', 'Monday'),
        tuesday: this.$t('common.week.tuesday', 'Tuesday'),
        wednesday: this.$t('common.week.wednesday', 'Wednesday'),
        thursday: this.$t('common.week.thursday', 'Thursday'),
        friday: this.$t('common.week.friday', 'Friday'),
        saturday: this.$t('common.week.saturday', 'Saturday'),
        sunday: this.$t('common.week.sunday', 'Sunday'),
      }
    },
    getMonthYear(): string {
      if (this.month === null || this.year === null) {
        return ''
      }
      return dateHelper.format(new Date(this.year, this.month, 1), 'MMMM yyyy', this.language)
    },
    columnStyle(): {
      minWidth: string
      maxWidth: string
    } {
      return { minWidth: 'calc(100% / 7)', maxWidth: 'calc(100% / 7)' }
    },
    effectiveAvailableSlots(): Slot[] {
      return this.availableSlots || []
    },
  },
  watch: {
    selectedSlot: {
      handler(newSlot) {
        if (newSlot?.end && this.localSelectedDay !== newSlot.end) {
          this.localSelectedDay = newSlot.end
        }
      },
      immediate: false,
    },
  },
  beforeMount() {
    const defaultDate = this.deliveryDate
      ? dateHelper.getDate(this.deliveryDate)
      : this.minDeliveryPromiseDate
        ? new Date(this.minDeliveryPromiseDate)
        : dateHelper.getDate()
    this.localSelectedDay = (defaultDate instanceof Date
      ? defaultDate
      : new Date(defaultDate)
    ).toISOString()
    this.currentDate = defaultDate instanceof Date ? defaultDate : new Date(defaultDate)
    this.$emit('daySelected', this.localSelectedDay)
    this.emitMonthChange()
  },
  methods: {
    getDays(): Array<{
      isCurrent: boolean
      isCurrentMonth: boolean
      date: Date
      isToday: boolean
      unavailable: {
        isPast: boolean
        isBeforeMin: boolean
        isUnavailable: boolean
        isUnavailableFromAPI: boolean
      }
    }> {
      const monthStart = startOfMonth(this.currentDate)

      this.month = getMonth(monthStart)
      this.year = getYear(monthStart)

      const dayNumInWeek = getDay(monthStart)

      const calendarStart = subDays(monthStart, dayNumInWeek !== 0 ? dayNumInWeek - 1 : 6)

      const endMonthDayNumInWeek = getDay(addDays(monthStart, getDaysInMonth(monthStart)))

      const calendarEnd = addDays(
        calendarStart,
        getDaysInMonth(monthStart) +
        (dayNumInWeek !== 0 ? dayNumInWeek - 1 : 6) +
        (endMonthDayNumInWeek !== 0 ? 7 - endMonthDayNumInWeek : 0),
      )

      const days = eachDayOfInterval({
        start: calendarStart,
        end: calendarEnd,
      })

      const mappedDays = days.map(date => {
        const today = dateHelper.getDate()
        const daysFromToday = differenceInCalendarDays(date, today)
        const unavailable = this.getDateUnavailableStatus(date, daysFromToday)

        const mappedDay = {
          isCurrent: compareAsc(this.currentDate, date) === 0,
          isCurrentMonth: date.getMonth() === this.currentDate.getMonth(),
          date: sub(date, { minutes: date.getTimezoneOffset() }),
          isToday: daysFromToday === 0,
          unavailable,
        }
        return mappedDay
      })
      return mappedDays
    },
    getSelectedDay(day: number, week: number) {
      return this.getDays()[day - 1 + 7 * (week - 1)]
    },
    isBeforePickingDate(calendarDayDate: Date): boolean {
      if (!this.pickingDates) {
        return false
      }

      try {
        const pickingDate = dateHelper.getDate(this.pickingDates?.start)
        if (isNaN(pickingDate.getTime()) || !calendarDayDate || isNaN(calendarDayDate.getTime())) {
          return false
        }
        const daysFromPickingDate = differenceInCalendarDays(calendarDayDate, pickingDate)
        return daysFromPickingDate < 0
      } catch (error) {
        return false
      }
    },
    handleNewSelectedDay(day: string) {
      const selectedDate = new Date(day)
      const today = dateHelper.getDate()
      const daysFromToday = differenceInCalendarDays(selectedDate, today)
      const unavailable = this.getDateUnavailableStatus(selectedDate, daysFromToday)

      if (unavailable.isUnavailable) {
        // Don't allow selection of unavailable dates
        return
      }

      this.localSelectedDay = day
      // Emit event to parent to fetch slots for selected day
      this.$emit('daySelected', day)
    },
    localGetDate(day: number, week: number): number {
      return getDate(this.getSelectedDay(day, week).date)
    },
    toPreviousMonth(): void {
      this.currentDate = subMonths(startOfMonth(this.currentDate), 1)
      this.$emit('clearSlots')
      this.emitMonthChange()
    },
    toNextMonth(): void {
      this.currentDate = addMonths(startOfMonth(this.currentDate), 1)
      this.$emit('clearSlots')
      this.emitMonthChange()
    },
    emitMonthChange(): void {
      const monthStart = startOfMonth(this.currentDate)
      const monthEnd = endOfMonth(this.currentDate)

      const startDate = dateHelper.format(monthStart, 'yyyy-MM-dd')
      const endDate = dateHelper.format(monthEnd, 'yyyy-MM-dd')

      this.$emit('monthChanged', {
        startDate,
        endDate,
        month: getMonth(monthStart),
        year: getYear(monthStart),
      })
    },
    isDayIsSelected(day: number, week: number): boolean {
      if (!this.localSelectedDay) {
        return false
      }

      try {
        const selectedDayDate = dateHelper.getDate(this.localSelectedDay)
        const calendarDay = this.getSelectedDay(day, week)
        const calendarDayDate = calendarDay?.date
        if (
          isNaN(selectedDayDate.getTime()) ||
          !calendarDayDate ||
          isNaN(calendarDayDate.getTime())
        ) {
          return false
        }
        const selectedDayOnly = selectedDayDate.toDateString()
        const calendarDayOnly = calendarDayDate.toDateString()

        return selectedDayOnly === calendarDayOnly
      } catch (error) {
        return false
      }
    },
    // @ts-ignore
    selectSlot(slot) {
      this.selectedSlotId = slot.id
      this.$emit('slotSelected', slot)
    },
    formatSlotTime(slot: { start: string; end: string }): string {
      // Get client timezone
      const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const startTime = dateHelper.getDate(slot.start).toLocaleTimeString(this.language, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: clientTimezone,
      })
      const endTime = dateHelper.getDate(slot.end).toLocaleTimeString(this.language, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: clientTimezone,
      })

      return `${startTime} - ${endTime}`
    },

    /**
     * Gets the unavailable status of a date with detailed information
     * @param {Date} date - The date to check
     * @param {number} daysFromToday - Pre-calculated days from today (for performance)
     * @returns {Object} - Object containing isPast, isBeforeMin, isUnavailable, and isUnavailableFromAPI flags
     */
    getDateUnavailableStatus(
      date: Date,
      daysFromToday: number,
    ): {
      isPast: boolean
      isBeforeMin: boolean
      isUnavailable: boolean
      isUnavailableFromAPI: boolean
    } {
      const isPast = daysFromToday < 0

      let isBeforeMin = false
      if (this.minDeliveryPromiseDate) {
        try {
          const minDate = new Date(this.minDeliveryPromiseDate)
          const daysFromMinDate = differenceInCalendarDays(date, minDate)
          isBeforeMin = daysFromMinDate < 0
        } catch (error) {
          isBeforeMin = false
        }
      } else {
        isBeforeMin = false
      }

      // Check if the date is in unavailableDates from API
      const dateStr = dateHelper.format(date, 'yyyy-MM-dd')
      const isUnavailableFromAPI = this.unavailableDates.some((unavailable) => {
        // Extract date part from ISO string (format: "2025-11-01T00:00:00.000Z" -> "2025-11-01")
        const unavailableDateStr = unavailable.date.split('T')[0]
        return unavailableDateStr === dateStr
      })

      return {
        isPast,
        isBeforeMin,
        isUnavailable: isPast || isBeforeMin || isUnavailableFromAPI,
        isUnavailableFromAPI,
      }
    },
    /**
     * Gets the reasons for an unavailable date from the API
     * @param {Date} date - The date to check
     * @returns {string[]} - Array of reasons for unavailability
     */
    getUnavailableReasonsForDate(date: Date): string[] {
      const dateStr = dateHelper.format(date, 'yyyy-MM-dd')
      const unavailableDate = this.unavailableDates.find((unavailable) => {
        const unavailableDateStr = unavailable.date.split('T')[0]
        return unavailableDateStr === dateStr
      })
      return unavailableDate?.reasons || []
    },

    validateSelection(): void {
      if (this.selectedSlotId) {
        const selectedSlot = this.effectiveAvailableSlots.find(
          (slot: Slot) => slot.id === this.selectedSlotId,
        )
        if (selectedSlot) {
          this.$emit('validateSlot', selectedSlot)
        }
      }
    },
  },
})
